import type { InfiniteData } from '@tanstack/react-query';
import type { MessageItem, Paginated } from './types';

export type MessageCache = Paginated<MessageItem> | InfiniteData<Paginated<MessageItem>, unknown>;

function isInfiniteMessageCache(
  cache: MessageCache,
): cache is InfiniteData<Paginated<MessageItem>, unknown> {
  return 'pages' in cache;
}

/** Page 1 is newest; render loaded pages oldest-to-newest. */
export function flattenMessagePages(
  data: InfiniteData<Paginated<MessageItem>, unknown> | undefined,
): MessageItem[] {
  return data ? [...data.pages].reverse().flatMap((page) => page.items) : [];
}

/** Append an optimistic reply to the newest page, regardless of cache shape. */
export function appendCachedMessage(cache: MessageCache, message: MessageItem): MessageCache {
  if (!isInfiniteMessageCache(cache)) {
    return { ...cache, items: [...cache.items, message], total: cache.total + 1 };
  }
  return {
    ...cache,
    pages: cache.pages.map((page, index) => ({
      ...page,
      items: index === 0 ? [...page.items, message] : page.items,
      total: page.total + 1,
    })),
  };
}

/**
 * Merge a server-pushed message into the newest loaded page.
 *
 * Realtime delivery can race the canonical query refetch (and Redis pub/sub
 * may redeliver the same event), so this operation is deliberately
 * idempotent. An existing row is replaced in place; a genuinely new row is
 * added to page 1 and every page's total is advanced once.
 */
export function upsertRealtimeMessage(cache: MessageCache, message: MessageItem): MessageCache {
  let found = false;
  const replace = (item: MessageItem): MessageItem => {
    if (item.id !== message.id) return item;
    found = true;
    return { ...item, ...message };
  };

  if (!isInfiniteMessageCache(cache)) {
    const items = cache.items.map(replace);
    return found
      ? { ...cache, items }
      : {
          ...cache,
          items: sortChronologically([...items, message]),
          total: cache.total + 1,
        };
  }

  const pages = cache.pages.map((page) => ({
    ...page,
    items: page.items.map(replace),
  }));
  if (found) return { ...cache, pages };

  return {
    ...cache,
    pages: pages.map((page, index) => ({
      ...page,
      items: index === 0 ? sortChronologically([...page.items, message]) : page.items,
      total: page.total + 1,
    })),
  };
}

function sortChronologically(items: MessageItem[]): MessageItem[] {
  return [...items].sort((a, b) => {
    const aTime = parseMessageTime(a);
    const bTime = parseMessageTime(b);
    if (aTime !== bTime) return aTime - bTime;
    return (a.createdAt ?? '').localeCompare(b.createdAt ?? '');
  });
}

function parseMessageTime(message: MessageItem): number {
  const parsed = Date.parse(message.sentAt ?? message.createdAt ?? '');
  return Number.isNaN(parsed) ? 0 : parsed;
}

/** Reconcile an optimistic id/status across every loaded history page. */
export function mapCachedMessages(
  cache: MessageCache,
  mapper: (message: MessageItem) => MessageItem,
): MessageCache {
  if (!isInfiniteMessageCache(cache)) {
    return { ...cache, items: cache.items.map(mapper) };
  }
  return {
    ...cache,
    pages: cache.pages.map((page) => ({ ...page, items: page.items.map(mapper) })),
  };
}
